import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { verificationService } from '../../../services/articleVerificationService';
import {
  Box,
  Container,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import FileUpload from '../../../components/FileUpload';
import VerificationResultsTable from '../../../components/VerificationResultsTable';
import { TaskStatus } from '../../../interfaces/core/ArticleVerification';

const ArticleVerificationPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0); // Progress bar value (0-100)
  const [uploadTime, setUploadTime] = useState<number | null>(null); // Timestamp when file was uploaded
  const [completedStatus, setCompletedStatus] = useState<TaskStatus | null>(null); // Store completed status after refresh

  // Check localStorage on mount, but validate the task exists
  useEffect(() => {
    const savedTaskId = localStorage.getItem('verificationTaskId');
    const savedUploadTime = localStorage.getItem('verificationUploadTime');
    
    if (savedTaskId && savedUploadTime) {
      const uploadTimestamp = parseInt(savedUploadTime, 10);
      const elapsed = Date.now() - uploadTimestamp;
      
      // If more than 1 minute has passed, check task status first
      if (elapsed >= 60000) {
        verificationService.getVerificationStatus(savedTaskId)
          .then((status) => {
            // Always clear localStorage and refresh flag after checking
            localStorage.removeItem('verificationTaskId');
            localStorage.removeItem('verificationUploadTime');
            localStorage.removeItem('verificationRefreshDone');
            
            // If task is completed, show results
            if (status.status === 'completed') {
              // Store completed status in state so it's available immediately
              setCompletedStatus(status);
              // Set the data in cache
              queryClient.setQueryData(['verificationStatus', savedTaskId], status);
              // Don't set activeTaskId - this will make shouldShowUpload true
            }
            // If task is not completed, don't refresh again - user can manually refresh if needed
          })
          .catch(() => {
            // Task doesn't exist, clear localStorage
            localStorage.removeItem('verificationTaskId');
            localStorage.removeItem('verificationUploadTime');
            localStorage.removeItem('verificationRefreshDone');
          });
        return;
      }
      
      // Less than 1 minute has passed, continue normally
      verificationService.getVerificationStatus(savedTaskId)
        .then(() => {
          // Task exists, set it as active
          setActiveTaskId(savedTaskId);
          setUploadTime(uploadTimestamp);
          // Calculate initial progress
          setProgress((elapsed / 60000) * 100);
        })
        .catch((error: any) => {
          // Task doesn't exist (404), clear localStorage
          console.log('Saved task not found, clearing localStorage');
          localStorage.removeItem('verificationTaskId');
          localStorage.removeItem('verificationUploadTime');
        });
    }
  }, [queryClient]);

  const statusQuery = useQuery<TaskStatus, Error>({
    queryKey: ['verificationStatus', activeTaskId] as const,
    queryFn: async () => {
      if (!activeTaskId) {
        throw new Error('No active task');
      }
      return await verificationService.getVerificationStatus(activeTaskId);
    },
    enabled: !!activeTaskId,
    refetchInterval: false, // Disable automatic polling
    refetchOnMount: true, // Enable refetch on mount to get data after refresh
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Progress bar timer - updates every second and refreshes page after 1 minute
  useEffect(() => {
    if (!uploadTime) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - uploadTime;
      const newProgress = Math.min((elapsed / 60000) * 100, 100);
      setProgress(newProgress);

      // After 1 minute, refresh the page ONCE
      if (elapsed >= 60000) {
        clearInterval(interval);
        // Set a flag to indicate refresh has been done
        localStorage.setItem('verificationRefreshDone', 'true');
        window.location.reload();
      }
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [uploadTime]);

  const handleFileUpload = async (file: File) => {
    try {
      const response = await verificationService.startVerification(file);
      
      // Clear old queries first
      queryClient.removeQueries({
        queryKey: ['verificationStatus']
      });
      
      // Set new task ID and upload time
      const now = Date.now();
      setActiveTaskId(response.task_id);
      setUploadTime(now);
      setProgress(0);
      
      // Save to localStorage
      localStorage.setItem('verificationTaskId', response.task_id);
      localStorage.setItem('verificationUploadTime', now.toString());
      
      // Fetch initial status
      queryClient.invalidateQueries({
        queryKey: ['verificationStatus', response.task_id]
      });

    } catch (error: any) {
      console.error('Error uploading file:', error);
      // Show error message to user
      const errorMessage = error?.response?.data?.detail || error?.message || 'Error uploading file. Please try again.';
      alert(errorMessage);
    }
  };


  // Use completedStatus if available (after refresh), otherwise use statusQuery.data
  const displayData = completedStatus || statusQuery.data;
  
  const shouldShowUpload = !activeTaskId || 
    statusQuery.isError || 
    (statusQuery.error as any)?.response?.status === 404 ||
    displayData?.status === 'completed' ||
    displayData?.status === 'error';

  return (
    <Container>
      {shouldShowUpload ? (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4" gutterBottom>
              Article Verification
            </Typography>
            {displayData?.status === 'completed' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Verification completed successfully! Upload a new file to start a new verification.
              </Alert>
            )}
          </Box>
          <FileUpload onFileSelect={handleFileUpload} />
          
          {/* Show previous results if completed */}
          {displayData?.status === 'completed' && displayData.results && displayData.results.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Verification Results
              </Typography>
              <VerificationResultsTable
                results={displayData.results}
                totalChecked={displayData.total_checked}
                totalMissing={displayData.total_missing}
              />
            </Box>
          )}
          
          {/* Debug: Show if completedStatus is set */}
          {process.env.NODE_ENV === 'development' && completedStatus && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
              <Typography variant="caption">
                Debug: completedStatus is set, results count: {completedStatus.results?.length || 0}
              </Typography>
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h4">
              Verification in Progress
            </Typography>
          </Box>

          {statusQuery.isLoading && !statusQuery.data ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, mb: 2 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                Starting verification process...
              </Typography>
            </Box>
          ) : statusQuery.error ? (
            <Alert severity="error">
              Error: Something went wrong
            </Alert>
          ) : (
            <>
              {uploadTime && (
                <Box sx={{ width: '100%', mb: 3 }}>
                  <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
                  <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'text.secondary' }}>
                    Page will refresh automatically in {Math.max(0, Math.ceil((60000 - (Date.now() - uploadTime)) / 1000))} seconds
                  </Typography>
                </Box>
              )}

              {displayData?.status === 'processing' && (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  py: 4,
                  mb: 2 
                }}>
                  <CircularProgress size={60} sx={{ mb: 2 }} />
                  <Typography variant="h6" sx={{ color: 'primary.main', mb: 1 }}>
                    Processing Articles...
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Checking articles against TecDoc API and database. Page will refresh automatically after 1 minute.
                  </Typography>
                  {displayData?.total_rows && displayData.total_rows > 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      Total articles to verify: {displayData.total_rows}
                    </Typography>
                  )}
                </Box>
              )}

              {displayData?.status === 'completed' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Verification completed successfully! Results are shown below.
                </Alert>
              )}

              {displayData?.results && displayData.results.length > 0 && (
                <VerificationResultsTable
                  results={displayData.results}
                  totalChecked={displayData.total_checked}
                  totalMissing={displayData.total_missing}
                />
              )}

              {displayData?.status === 'error' && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Error: {displayData.error}
                </Alert>
              )}
            </>
          )}
        </Box>
      )}
    </Container>
  );
};

export default ArticleVerificationPage;